/**
 * NarratorBackdrop — immersive full-screen overlay for Narrator-persona scenes.
 *
 * Why this exists
 *   Narrator scenes are pure story moments ("8:47 AM — Maria walks in") that
 *   don't correspond to any real product screen. Before this overlay, the
 *   tour would land on the Landing page or the Tours index, which leaked
 *   irrelevant UI behind the narrator panel and broke the cinematic feel.
 *
 *   Now: whenever the current scene's persona is "Narrator" or "System" the
 *   backdrop slides in on top of everything else — a dark, slowly-drifting
 *   gradient with a subtle vignette and the District logo watermarked low.
 *   The narrator panel keeps its usual position; the backdrop sits behind it.
 *
 * Elapsed-time badge (closing narrator scene)
 *   If the scene declares `showElapsedBadge: true`, the backdrop replaces
 *   the "Listen to the narrator" tagline with a big monospace stopwatch
 *   badge — "Program went live in ⏱ 4:12" — computed live from the
 *   tour's real start timestamp. Turns the pitch from "it's fast" into
 *   the actual clock the funder just watched tick.
 */
import React, { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';
import { useTour } from '../../context/TourContext';

const NARRATOR_ROLES = new Set(['Narrator', 'System']);

function formatElapsed(ms) {
  if (ms == null || ms < 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function NarratorBackdrop() {
  const t = useTour();
  const [now, setNow] = useState(() => Date.now());

  // Only tick the wall-clock when a narrator scene wants the badge visible.
  // Everywhere else, no interval work — the backdrop is a pure render.
  const wantsBadge = !!t.currentScene?.showElapsedBadge && !!t.tourStartedAt;
  useEffect(() => {
    if (!wantsBadge) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [wantsBadge]);

  if (!t.isActive || !t.currentScene) return null;
  const role = t.currentScene.persona?.role;
  if (!NARRATOR_ROLES.has(role)) return null;

  const elapsedLabel = wantsBadge ? formatElapsed(now - t.tourStartedAt) : null;
  const badgePrefix = t.currentScene.elapsedBadgePrefix || 'Program went live in';

  return (
    <div
      data-testid="tour-narrator-backdrop"
      className="fixed inset-0 z-[9990] overflow-hidden animate-fade-in"
      style={{ background: 'radial-gradient(circle at 30% 25%, #1E3A5F 0%, #0f1b2d 55%, #060b15 100%)' }}
    >
      {/* Subtle drifting glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 75% 70%, rgba(46,134,171,0.20) 0%, transparent 55%)',
          animation: 'tour-drift 18s ease-in-out infinite alternate',
        }}
      />
      {/* Soft starfield */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.5) 0.5px, transparent 0.5px)',
          backgroundSize: '4px 4px',
        }}
      />
      {/* District watermark (lower-third so the narrator panel sits cleanly above it) */}
      <div className="absolute left-1/2 top-[28%] -translate-x-1/2 flex flex-col items-center text-white/90">
        <img
          src="/district-logo.png"
          alt=""
          aria-hidden
          className="h-16 mb-4 brightness-0 invert opacity-95"
        />
        <div className="text-[10.5px] uppercase tracking-[0.25em] text-sky-300/80 font-semibold">Guided Tour</div>

        {elapsedLabel ? (
          <div
            data-testid="tour-elapsed-badge"
            className="mt-6 flex items-center gap-3 px-6 py-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] backdrop-blur-sm shadow-[0_0_40px_-10px_rgba(52,211,153,0.5)]"
          >
            <Timer className="w-6 h-6 text-emerald-300" />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-200/70 font-semibold">
                {badgePrefix}
              </span>
              <span
                data-testid="tour-elapsed-value"
                className="text-[38px] font-mono font-semibold text-emerald-100 tabular-nums leading-none mt-1"
                style={{ textShadow: '0 0 24px rgba(110, 231, 183, 0.35)' }}
              >
                {elapsedLabel}
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-1 text-[12.5px] text-blue-200/60">Listen to the narrator — the workflow begins next</div>
        )}
      </div>
    </div>
  );
}
